import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import './AboutCommunity.css';
import sdgBadge  from '../../assets/sdg_badge.png';
import whoWeAre2 from '../../assets/recap1.jpg';
import whoWeAre1 from '../../assets/WhoWeAre1.jpg';
import whoWeAre3 from '../../assets/WhoWeAre3.jpg';

const sdgGoals = [
  { code: 'SDG 4',  label: 'Quality Education',           color: '#C5192D' },
  { code: 'SDG 8',  label: 'Decent Work & Growth',        color: '#A21942' },
  { code: 'SDG 9',  label: 'Innovation & Infrastructure', color: '#FD6925' },
  { code: 'SDG 17', label: 'Partnerships for the Goals',  color: '#19486A' },
];

const visionPillars = [
  { num: '01', title: 'Education',          desc: 'Building quantum literacy and skills across Kenya through accessible learning pathways.' },
  { num: '02', title: 'Research',           desc: 'Bridging academic research with real-world quantum applications and discoveries.' },
  { num: '03', title: 'Community Outreach', desc: 'Connecting and growing a diverse community of quantum enthusiasts across Africa.' },
];

const AboutCommunity = () => {
  const stats = [
    { number: '500+', label: 'Active Members'    },
    { number: '20+',  label: 'Themed Events'     },
    { number: '15+',  label: 'Industry Partners' },
  ];

  const [statsIndex, setStatsIndex] = useState(0);
  const nextStat = () => setStatsIndex((prev) => (prev + 1) % stats.length);
  const prevStat = () => setStatsIndex((prev) => (prev - 1 + stats.length) % stats.length);

  return (
    <>
      <Helmet>
        <title>About Us | QuantumKE</title>
        <meta name="description" content="Learn about QuantumKE's vision, mission, and values. We advance inclusive quantum science and technology across Kenya and Africa, connecting talent, research, and real-world innovation." />
        <link rel="canonical" href="https://quantumke.org/about" />
      </Helmet>

      {/* ── 1. BANNER ── */}
      <div className="about-banner">
        <div className="about-banner-glow" aria-hidden="true" />
        <div className="about-banner-inner">
          <div className="about-banner-text">
            <h1 className="about-banner-title">Who We Are</h1>
            <p className="about-banner-sub">
              A community of researchers, innovators, and enthusiasts building Africa's quantum technology ecosystem — one collaboration at a time.
            </p>
          </div>
        </div>
      </div>

      {/* ── 2. VISION ── */}
      <section className="vision-section">
        <div className="vision-inner">
          <div className="vision-left">
            <span className="vision-eyebrow">Our Vision</span>
            <p className="vision-statement">
              To unlock <em>Africa's Potential</em> in Quantum Science and Technology.
            </p>
          </div>
          <div className="vision-pillars">
            {visionPillars.map((p) => (
              <div className="vision-pillar" key={p.num}>
                <span className="pillar-num">{p.num}</span>
                <h3 className="pillar-title">{p.title}</h3>
                <p className="pillar-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. MISSION + SDG ── */}
      <section className="about-mission-section">
        <div className="about-mission-watermark" aria-hidden="true">MISSION</div>
        <div className="about-mission-layout">
          <div className="about-mission-inner">
            <div className="about-mission-eyebrow">Our Mission</div>
            <h2 className="about-mission-headline">
              Bridging the Quantum Gap across<br />Kenya and Africa.
            </h2>
            <p className="about-mission-body">
              We advance inclusive and accessible quantum science and technology by driving innovation,
              empowering diverse talent, and supporting startups through open pathways to learning,
              collaboration, and real-world impact. Aligned with Africa's Agenda 2063 and the SDGs,
              QuantumKE serves as a neutral, collaborative platform where professionals and emerging
              learners connect, contribute, and shape a transformative, equitable quantum future.
            </p>
          </div>
          <div className="about-mission-sdg">
            <img
              src={sdgBadge}
              alt="UN Sustainable Development Goals"
              className="mission-sdg-img"
            />
            <p className="mission-sdg-aligned">Aligned with</p>
            <div className="mission-sdg-goals">
              {sdgGoals.map((g) => (
                <span
                  key={g.code}
                  className="sdg-goal-tag"
                  style={{ backgroundColor: g.color }}
                >
                  <strong>{g.code}</strong> — {g.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. COMMUNITY IN ACTION + QUOTE ── */}
      <section className="cia-section">
        <div className="cia-wrapper">

          <div className="cia-header">
            <span className="cia-eyebrow">Community in Action</span>
            <h2 className="cia-title">Real people. Real impact.</h2>
          </div>

          <div className="cia-gallery">
            <div className="cia-gallery-main">
              <img src={whoWeAre2} alt="QuantumKE community gathering" loading="lazy" />
            </div>
            <div className="cia-gallery-side">
              <img src={whoWeAre1} alt="Community discussion session" loading="lazy" />
              <img src={whoWeAre3} alt="Community collaboration" loading="lazy" />
            </div>
          </div>

          <div className="cia-pullquote">
            <svg className="cia-quote-icon" viewBox="0 0 40 32" fill="none" aria-hidden="true">
              <path d="M0 32V19.2C0 8.533 5.333 2.133 16 0l2.4 3.733C12.267 5.6 9.2 9.333 8 15.467H16V32H0ZM24 32V19.2C24 8.533 29.333 2.133 40 0l2.4 3.733C36.267 5.6 33.2 9.333 32 15.467H40V32H24Z" fill="currentColor"/>
            </svg>
            <blockquote className="cia-quote-text">
              Together, we're not just learning about quantum technology — we're building the quantum future of Africa.
            </blockquote>
          </div>

        </div>
      </section>

      {/* ── 5. STATS ── */}
      <div className="about-stats-section">
        <div className="quantum-stats-slider">
          <button onClick={prevStat} className="slider-arrow" aria-label="Previous stat"><ChevronLeft /></button>
          <div className="quantum-stat-card">
            <div className="quantum-stat-number">{stats[statsIndex].number}</div>
            <div className="quantum-stat-label">{stats[statsIndex].label}</div>
          </div>
          <button onClick={nextStat} className="slider-arrow" aria-label="Next stat"><ChevronRight /></button>
        </div>
        <div className="about-stats-row">
          {stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="about-stat-divider" aria-hidden="true" />}
              <div className="about-stat-item">
                <div className="about-stat-number">{stat.number}</div>
                <div className="about-stat-label">{stat.label}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};

export default AboutCommunity;
